import { useState } from "react"
import { AlertBox } from "./AlertBox"
import SharingOptions from "./SharingOptions"
import Policies from "../../Modules/Settings/Policies/Policies"
import { Check, X } from "lucide-react"
import { getThreadTypesArr } from "../../Store/Reducers"
import { getThreadNameFromTypeID } from "../../Utils/Utilities"
import { useSelector } from "react-redux"
import { Textarea } from "../UI/textarea"
import { Label } from "../../Components/UI/label";

const CompleteThreadDialog = ({
  typeID = 2,
  open,
  onOpenChange,
  connID,
  onComplete,
  showFeedbackOption = false  // New prop to control feedback visibility
}) => {
  const [shareByEmail, setShareByEmail] = useState(false)
  const [shareByWhatsApp, setShareByWhatsApp] = useState(false)  // New state for WhatsApp sharing
  const [takeFeedback, setTakeFeedback] = useState(false)  // New state for feedback
  const [completionNote, setCompletionNote] = useState("") // New state for completion note
  const [emailConfigOpen, setEmailConfigOpen] = useState(false)
  const [whatsAppConfigOpen, setWhatsAppConfigOpen] = useState(false)  // New state for WhatsApp config dialog
  const [googleRevLinkOpen, setGoogleRevLinkOpen] = useState(false)
  const threadTypesData = useSelector(getThreadTypesArr);

  const handleEmailShareChange = (checked) => {
    setShareByEmail(checked)
    // If email sharing is disabled, also disable feedback
    if (!checked) {
      setTakeFeedback(false)
    }
  }

  const handleWhatsAppShareChange = (checked) => {
    setShareByWhatsApp(checked)
  }

  const handleFeedbackChange = (checked) => {
    setTakeFeedback(checked)
  }

  const handleSettingsClick = (dialogType) => {
    if (dialogType === "EMAIL") {
      setEmailConfigOpen(true)
    } else if (dialogType === "GOOGLE_REVIEW_LINK") {
      setGoogleRevLinkOpen(true)
    } else if (dialogType === "WHATSAPP") {
      setWhatsAppConfigOpen(true)
    }
  }

  const handleAction = (actionValue) => {
    if (actionValue === 1) {
      // Pass email, WhatsApp sharing, feedback states and note to parent
      onComplete({
        shareByEmail,
        shareByWhatsApp,
        takeFeedback: showFeedbackOption ? takeFeedback : false,
        completionNote: (typeID === 3 && shareByEmail) ? completionNote : ""
      })
    }
  }

  return (
    <>
      <AlertBox
        open={open}
        onOpenChange={onOpenChange}
        title={`Complete ${getThreadNameFromTypeID(typeID, threadTypesData, "SnSng")}`}
        content={
          <div>
            <p className="mb-4">{`Are you sure you want to mark this ${getThreadNameFromTypeID(typeID, threadTypesData, "SnSng")} as complete?`}</p>
            {[2, 3].includes(typeID) && <SharingOptions
              connID={connID}
              isEmailShared={shareByEmail}
              onEmailShareChange={handleEmailShareChange}
              showInAddMode={true}
              isEditMode={false}
              onSettingsClick={(dialogType) => handleSettingsClick(dialogType)}
              showFeedbackOption={showFeedbackOption}
              isFeedbackEnabled={takeFeedback}
              onFeedbackChange={handleFeedbackChange}
              showWhatsAppOption={true}
              isWhatsAppShared={shareByWhatsApp}
              onWhatsAppShareChange={handleWhatsAppShareChange}
              className="space-y-3"
            />}

            {/* Notes Field: Only for Tickets (TypeID 3) and when Email Sharing is enabled */}
            {typeID === 3 && shareByEmail && (
              <div className="mt-3 space-y-1.5">
                <Label>
                  Closing Remarks
                </Label>
                <Textarea
                  value={completionNote}
                  onChange={(e) => setCompletionNote(e.target.value)}
                  placeholder="e.g. Explanation provided to the client and they are satisfied with the solution."
                  maxLength={1000}
                  className="min-h-[100px] text-sm transition-all duration-200 bg-white sahi-input resize-none"
                />
              </div>
            )}
          </div>
        }
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            value: 0,
            className: "w-full sm:w-auto",
            icon: <X className="h-4 w-4" />
          },
          {
            label: "Yes, Complete",
            variant: "action",
            value: 1,
            className: "w-full sm:w-auto",
            icon: <Check className="h-4 w-4" />
          }
        ]}
        onAction={handleAction}
        className="lg:max-w-xl"
      />

      {/* Email config dialog */}
      <Policies
        open={emailConfigOpen}
        onOpenChange={setEmailConfigOpen}
        configType="EMAIL"
      />

      {/* WhatsApp config dialog */}
      <Policies
        open={whatsAppConfigOpen}
        onOpenChange={setWhatsAppConfigOpen}
        configType="WHATSAPP"
      />

      {/* Google review link config dialog */}
      <Policies
        open={googleRevLinkOpen}
        onOpenChange={setGoogleRevLinkOpen}
        configType="GOOGLE_REVIEW_LINK"
      />
    </>
  )
}

export default CompleteThreadDialog